import { Box, ButtonBase, Tooltip, useTheme } from '@mui/material';
import { alpha, keyframes, type Theme } from '@mui/material/styles';
import { Check, CircleMinus } from 'lucide-react';
import { useIntl } from 'react-intl';
import {
    COURSE_STAGE_VALUES,
    courseStageLabelId,
    type CourseStage,
} from '../model';

/** Mark and name sizes: `row` for a list row, `large` for the patient header. */
type StripSize = 'row' | 'large';

type Metrics = {
    /** An open stage's mark: a small ring. */
    todoMark: number;
    /** A skipped stage's mark: a ring with a dash. */
    skippedMark: number;
    /**
     * A done stage's mark: a filled disc with a check. It narrows, still
     * round, where a narrow strip leaves its cell less room.
     */
    doneMark: number;
    /**
     * The current stage's mark while it is in progress: a ring around a dot,
     * with a slowly breathing glow so the next step stands out in the row.
     * Once marked done it is drawn as done.
     */
    currentMark: number;
    /**
     * The dot inside the in-progress ring. Whole pixels, and even against the
     * ring's inner width, so the dot sits exactly centred.
     */
    currentDot: number;
    track: number;
    labelSize: number;
};

const METRICS: Record<StripSize, Metrics> = {
    row: {
        todoMark: 12,
        skippedMark: 14,
        doneMark: 20,
        currentMark: 20,
        currentDot: 8,
        track: 26,
        labelSize: 11,
    },
    large: {
        todoMark: 14,
        skippedMark: 16,
        doneMark: 24,
        currentMark: 24,
        currentDot: 10,
        track: 30,
        labelSize: 13,
    },
};

/** The in-progress ring's glow swells and settles, slowly, while the stage is worked. */
const breathe = keyframes`
    0%, 100% { box-shadow: 0 0 0 2px var(--halo), 0 0 4px var(--glow); }
    50% { box-shadow: 0 0 0 5px var(--halo), 0 0 12px var(--glow); }
`;

/** The in-progress ring's stroke, in whole pixels. */
const RING_BORDER = 2;

type StageState = 'current' | 'done' | 'skipped' | 'todo';

/** What a mark that is not the current stage's says it is, on hover. */
const MARK_STATE_LABELS = {
    done: 'patientList.stage.markState.done',
    skipped: 'patientList.stage.markState.skipped',
    todo: 'patientList.stage.markState.todo',
} as const satisfies Record<Exclude<StageState, 'current'>, string>;

const labelColor = (theme: Theme, state: StageState, isDone: boolean) => {
    if (state === 'current') {
        return theme.palette.text.primary;
    }
    return isDone
        ? theme.palette.text.secondary
        : theme.palette.action.disabled;
};

type CourseStageStripProps = {
    stage: CourseStage;
    done: readonly CourseStage[];
    size?: StripSize;
    /**
     * Whether each stage is named beneath its mark. Without names the strip
     * is marks and the current stage's glyph only, for where the stage is
     * named elsewhere on screen; it still reads out in full to assistive
     * technology and in its tooltip.
     */
    labels?: boolean;
    /**
     * Marks a stage done or undone. Omitted where the strip is a read-only
     * summary (the list rows); supplied in the panel, where the strip is the
     * checklist the user ticks off.
     */
    onToggleDone?: (stage: CourseStage) => void;
    /**
     * Makes a stage the current one. Where supplied, choosing a stage that is
     * not current selects it, and only the current stage is marked done or
     * not done.
     */
    onSelect?: (stage: CourseStage) => void;
    disabled?: boolean;
};

/**
 * Every course stage, always all of them, in course order: a check on a filled
 * disc once the user has marked it done, a glowing ring for the stage the
 * course is on while it is in progress, a dash for a stage before it that
 * nobody marked done, and a small ring for the rest. Each mark names its stage
 * and state on hover.
 *
 * Not a progress bar. A course takes the stages it needs (contouring alone
 * is a whole course), so no stage is ever shown done because of its position:
 * a stage is done only when someone marked it done, and the track between the
 * marks never fills. Done and current are separate facts and the strip never
 * conflates them: the stage being worked shows as in progress until someone
 * marks it done.
 *
 * One accent colour for every stage rather than each stage's own, because
 * the strip answers "what has this course finished", not "which stage is
 * this". The stage's own colour identifies it wherever it is named.
 */
export default function CourseStageStrip({
    stage,
    done,
    size = 'row',
    labels = true,
    onToggleDone,
    onSelect,
    disabled = false,
}: CourseStageStripProps) {
    const intl = useIntl();
    const theme = useTheme();
    const metrics = METRICS[size];
    const accent = theme.palette.primary.main;

    const label = (value: CourseStage) =>
        intl.formatMessage({ id: courseStageLabelId(value) });

    const summary = intl.formatMessage(
        { id: 'patientList.stage.stripSummary' },
        {
            stage: label(stage),
            done: done.length,
            total: COURSE_STAGE_VALUES.length,
        }
    );

    const currentIndex = COURSE_STAGE_VALUES.indexOf(stage);
    const stageState = (
        segment: CourseStage,
        index: number,
        isDone: boolean
    ): StageState => {
        if (segment === stage) {
            return 'current';
        }
        if (isDone) {
            return 'done';
        }
        return index < currentIndex ? 'skipped' : 'todo';
    };
    const stateLabel = (
        segment: CourseStage,
        state: StageState,
        isDone: boolean
    ) => {
        const id =
            state === 'current'
                ? isDone
                    ? 'patientList.stage.markState.currentDone'
                    : 'patientList.stage.markState.inProgress'
                : MARK_STATE_LABELS[state];
        return intl.formatMessage({ id }, { stage: label(segment) });
    };

    const markSx = (segment: CourseStage, state: StageState) => ({
        className: 'course-stage-mark',
        'data-stage': segment,
        'data-state': state,
    });

    const mark = (segment: CourseStage, state: StageState, isDone: boolean) => {
        if (isDone) {
            return (
                <Box
                    component="span"
                    {...markSx(segment, state)}
                    sx={{
                        position: 'relative',
                        flexShrink: 0,
                        width: metrics.doneMark,
                        maxWidth: 'calc(100% - 4px)',
                        aspectRatio: '1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        backgroundColor: accent,
                        color: theme.palette.primary.contrastText,
                    }}
                >
                    <Check size="66%" strokeWidth={3} aria-hidden />
                </Box>
            );
        }
        if (state === 'current') {
            const dotInset =
                (metrics.currentMark - 2 * RING_BORDER - metrics.currentDot) /
                2;
            return (
                <Box
                    component="span"
                    {...markSx(segment, state)}
                    sx={{
                        position: 'relative',
                        flexShrink: 0,
                        width: metrics.currentMark,
                        height: metrics.currentMark,
                        boxSizing: 'border-box',
                        borderRadius: '50%',
                        border: `${RING_BORDER}px solid ${accent}`,
                        '--halo': alpha(accent, 0.18),
                        '--glow': alpha(accent, 0.45),
                        boxShadow: '0 0 0 3px var(--halo), 0 0 8px var(--glow)',
                        animation: `${breathe} 2.4s ease-in-out infinite`,
                        '@media (prefers-reduced-motion: reduce)': {
                            animation: 'none',
                        },
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            position: 'absolute',
                            // Offsets in whole pixels: a percentage size or
                            // flex centring rounds the dot off centre.
                            top: dotInset,
                            left: dotInset,
                            width: metrics.currentDot,
                            height: metrics.currentDot,
                            borderRadius: '50%',
                            backgroundColor: theme.palette.primary.light,
                        }}
                    />
                </Box>
            );
        }
        if (state === 'skipped') {
            return (
                <Box
                    component="span"
                    {...markSx(segment, state)}
                    sx={{
                        position: 'relative',
                        flexShrink: 0,
                        display: 'flex',
                        color: alpha(theme.palette.text.secondary, 0.6),
                    }}
                >
                    <CircleMinus size={metrics.skippedMark} aria-hidden />
                </Box>
            );
        }
        return (
            <Box
                component="span"
                {...markSx(segment, state)}
                sx={{
                    position: 'relative',
                    flexShrink: 0,
                    width: metrics.todoMark,
                    height: metrics.todoMark,
                    boxSizing: 'border-box',
                    borderRadius: '50%',
                    border: `2px solid ${alpha(theme.palette.text.secondary, 0.4)}`,
                    backgroundColor: 'transparent',
                }}
            />
        );
    };

    const cells = COURSE_STAGE_VALUES.map((segment, index) => {
        const isDone = done.includes(segment);
        const state = stageState(segment, index, isDone);
        // The track stops short of every mark rather than running under it.
        const markSize = {
            current: metrics.currentMark,
            done: metrics.doneMark,
            skipped: metrics.skippedMark,
            todo: metrics.todoMark,
        }[state];
        const trackHalf = `calc(50% - ${markSize / 2 + 2}px)`;

        const body = (
            <>
                <Box
                    component="span"
                    sx={{
                        position: 'relative',
                        width: '100%',
                        height: metrics.track,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {/* One neutral track through every mark. It groups the
                        stages and never fills: a filled run would claim the
                        stages between two marks were passed through. */}
                    {index > 0 && (
                        <Box
                            component="span"
                            sx={{
                                position: 'absolute',
                                left: 0,
                                width: trackHalf,
                                top: metrics.track / 2 - 1,
                                height: 2,
                                bgcolor: 'divider',
                            }}
                        />
                    )}
                    {index < COURSE_STAGE_VALUES.length - 1 && (
                        <Box
                            component="span"
                            sx={{
                                position: 'absolute',
                                right: 0,
                                width: trackHalf,
                                top: metrics.track / 2 - 1,
                                height: 2,
                                bgcolor: 'divider',
                            }}
                        />
                    )}
                    {mark(segment, state, isDone)}
                </Box>
                {labels && (
                    <Box
                        component="span"
                        sx={{
                            fontSize: metrics.labelSize,
                            fontWeight: state === 'current' ? 600 : 400,
                            color: labelColor(theme, state, isDone),
                            // Stage names wrap rather than run into the
                            // neighbouring stage when the cell is narrow.
                            textAlign: 'center',
                            lineHeight: 1.3,
                            px: 0.25,
                        }}
                    >
                        {label(segment)}
                    </Box>
                )}
            </>
        );

        const cellSx = {
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '5px',
        } as const;

        if (onToggleDone === undefined) {
            return (
                <Tooltip
                    key={segment}
                    title={stateLabel(segment, state, isDone)}
                >
                    <Box component="span" sx={cellSx}>
                        {body}
                    </Box>
                </Tooltip>
            );
        }

        const selects = onSelect !== undefined && state !== 'current';
        const actionLabel = intl.formatMessage(
            {
                id: selects
                    ? 'patientList.stage.select'
                    : 'patientList.stage.toggleDone',
            },
            { stage: label(segment) }
        );
        return (
            <Tooltip key={segment} title={actionLabel}>
                {/* The whole column is the hit area, the name included; the
                    mark inside carries the paint. */}
                <ButtonBase
                    disabled={disabled}
                    aria-pressed={selects ? undefined : isDone}
                    aria-label={actionLabel}
                    onClick={() =>
                        selects ? onSelect(segment) : onToggleDone(segment)
                    }
                    sx={{
                        ...cellSx,
                        // ButtonBase centres its content; the marks must sit on
                        // one line whether a name wraps or not.
                        justifyContent: 'flex-start',
                        borderRadius: 1,
                        py: 0.25,
                        '&:hover .course-stage-mark': {
                            borderColor: theme.palette.primary.light,
                            color: theme.palette.primary.light,
                        },
                    }}
                >
                    {body}
                </ButtonBase>
            </Tooltip>
        );
    });

    const stripSx = { display: 'flex', minWidth: 0, width: '100%' } as const;

    if (onToggleDone !== undefined) {
        return (
            <Box
                component="span"
                role="group"
                aria-label={summary}
                sx={stripSx}
            >
                {cells}
            </Box>
        );
    }

    return (
        <Box component="span" role="img" aria-label={summary} sx={stripSx}>
            {cells}
        </Box>
    );
}
